import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { CustomerVo } from "@zhihaoscm/entity";
import { MEMBER_LEVEL } from "@zhihaoscm/meta";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    BaseItem as ZhBaseItem,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { ACTIVATION_CODE_STATE, ERROR_MESSAGE } from "@/meta/customer/activationCode";
import { OPEN_MODE } from "@/meta/customer/recharge";
import { dateFormat } from "@/utils";
import { getInfoFromCustomerJsonInfo } from "@/utils/getInfoFromCustomer";
import { formatNumber } from "@/utils/number";
import { Permission } from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";

export interface RechargeManageQuery {
    page: number;
    size: number;
    id?: string;
    customerKeyWord?: string;
    memberLevel?: number;
    openMode?: number;
    activationCode?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface RechargeManageRow {
    id: number | string;
    customerInfo?: { name?: string; realName?: string; mobile?: string; code?: string };
    memberLevel?: number | string;
    memberDay?: number | string;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

export interface RechargeManageResult {
    list?: RechargeManageRow[];
    total?: number;
}

export interface RechargeActivationCodeInfo {
    code?: string;
    level?: number;
    duration?: number;
    state?: number;
    hasBind?: number;
    customerInfo?: { id?: number; code?: string; realName?: string; institutionName?: string; mobile?: string };
}

export interface RechargeCustomerOption {
    id: number;
    label: string;
}

/** 复用 Vue 充值记录分页接口。 */
export async function loadRechargeManageRecords(query: RechargeManageQuery): Promise<RechargeManageResult> {
    const { data } = await request.service.get<RechargeManageResult>("/admin/member-open-record/paging", {
        params: query
    });
    return data;
}

/** 根据 Vue 兑换弹窗契约查询激活码详情。 */
export async function findRechargeActivationCode(code: string): Promise<RechargeActivationCodeInfo> {
    const { data } = await request.service.get<RechargeActivationCodeInfo>(`/admin/activation/code/find/${code}`);
    return data;
}

/** 将 Vue 客户远程选择结果转换成 React 弹窗所需的选项。 */
export async function searchRechargeCustomers(searchParam: string): Promise<RechargeCustomerOption[]> {
    const { data: customers } = await request.service.get<CustomerVo[]>("/admin/customer/shipping/selector", {
        params: { searchParam }
    });
    return customers.map((item: CustomerVo) => ({
        id: item.customer.id,
        label: getInfoFromCustomerJsonInfo(item.customer)
    }));
}

/** 沿用 Vue 激活码兑换接口及 `{ code, customerId }` payload。 */
export async function exchangeRechargeActivationCode(params: { code: string; customerId: number }) {
    const { data } = await request.service.get<{ errorCode: number }>("/admin/activation/code/exchange", { params });
    return data;
}

const customerText = (customer?: RechargeManageRow["customerInfo"]) =>
    customer
        ? [customer.name, customer.realName, customer.mobile, customer.code].filter(Boolean).join("|")
        : DEFAULT_VALUE;

/** 联运用户充值记录列表，保留 Vue 查询、兑换弹窗、字段、空态和失败反馈。 */
export function RechargeManagePage({
    loadRecords = loadRechargeManageRecords,
    findCode = findRechargeActivationCode,
    searchCustomers = searchRechargeCustomers,
    exchangeCode = exchangeRechargeActivationCode
}: {
    loadRecords?: (query: RechargeManageQuery) => Promise<RechargeManageResult>;
    findCode?: (code: string) => Promise<RechargeActivationCodeInfo>;
    searchCustomers?: (searchParam: string) => Promise<RechargeCustomerOption[]>;
    exchangeCode?: (params: { code: string; customerId: number }) => Promise<{ errorCode: number }>;
}) {
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<RechargeManageRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<RechargeManageQuery>({ page: 1, size: 10 });
    const [idKeyword, setIdKeyword] = useState("");
    const [keyword, setKeyword] = useState("");
    const [memberLevel, setMemberLevel] = useState("");
    const [openMode, setOpenMode] = useState("");
    // 保留旧 Vue ProTable 的激活码筛选草稿，查询时再写入分页请求参数。
    const [activationCodeKeyword, setActivationCodeKeyword] = useState("");
    const [error, setError] = useState("");
    const [exchangeOpen, setExchangeOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [activationCode, setActivationCode] = useState("");
    const [activationInfo, setActivationInfo] = useState<RechargeActivationCodeInfo | null>(null);
    const [customerOptions, setCustomerOptions] = useState<RechargeCustomerOption[]>([]);
    const [customerId, setCustomerId] = useState<number>();
    // 兑换表单错误按字段维护，统一渲染到对应 Form.Item 下方。
    const [activationError, setActivationError] = useState("");
    const [customerError, setCustomerError] = useState("");
    const [customerSearchError, setCustomerSearchError] = useState("");
    const [exchangeLoading, setExchangeLoading] = useState(false);
    const customerSearchRequestRef = useRef(0);

    const load = async (nextQuery: RechargeManageQuery) => {
        try {
            setError("");
            const result = await loadRecords(nextQuery);
            setRecords(result.list || []);
            setTotal(result.total || 0);
        } catch {
            setRecords([]);
            setTotal(0);
            setError("充值记录加载失败");
        }
    };

    useEffect(() => {
        void load(query);
    }, [query, loadRecords]);

    /** 清空 Vue 弹窗在关闭或重新打开时不应残留的表单和查询状态。 */
    const resetExchange = () => {
        setActivationCode("");
        setActivationInfo(null);
        setCustomerOptions([]);
        setCustomerId(undefined);
        customerSearchRequestRef.current += 1;
        setActivationError("");
        setCustomerError("");
        setCustomerSearchError("");
        setConfirmOpen(false);
    };

    const openExchange = () => {
        resetExchange();
        setExchangeOpen(true);
    };

    const closeExchange = () => {
        setExchangeOpen(false);
        resetExchange();
    };

    /** 只有完整的 20 位激活码才发起 Vue 详情查询。 */
    const handleActivationCodeChange = async (value: string) => {
        setActivationCode(value);
        setActivationInfo(null);
        setCustomerOptions([]);
        setCustomerId(undefined);
        customerSearchRequestRef.current += 1;
        setActivationError("");
        setCustomerError("");
        if (!/^[A-Za-z0-9]{20}$/.test(value)) return;

        try {
            const info = await findCode(value);
            setActivationInfo(info);
            if (info.state === ACTIVATION_CODE_STATE.UNUSED.code && info.hasBind) {
                setCustomerId(info.customerInfo?.id);
            }
        } catch {
            setActivationError("激活码查询失败");
        }
    };

    /** 空关键词也读取默认候选，兼容输入框获得焦点时展示账号列表。 */
    const handleCustomerSearch = async (value: string) => {
        setCustomerSearchError("");
        setCustomerError("");
        const keyword = value.trim();
        const requestId = ++customerSearchRequestRef.current;
        try {
            const options = await searchCustomers(keyword);
            if (requestId === customerSearchRequestRef.current) setCustomerOptions(options);
        } catch {
            if (requestId !== customerSearchRequestRef.current) return;
            setCustomerOptions([]);
            setCustomerSearchError("关联账号搜索失败");
        }
    };

    const validateExchange = () => {
        if (!/^[A-Za-z0-9]{20}$/.test(activationCode)) {
            setActivationError("只允许输入字母和数字，长度为20");
            return false;
        }
        if (!activationInfo) {
            setActivationError("该激活码不存在");
            return false;
        }
        if (activationInfo.state === ACTIVATION_CODE_STATE.USED.code) {
            setActivationError("该激活码已兑换");
            return false;
        }
        if (activationInfo.state === ACTIVATION_CODE_STATE.OVERDUE.code) {
            setActivationError("该激活码已过期");
            return false;
        }
        if (!customerId) {
            setCustomerError("请选择关联账号");
            return false;
        }
        return true;
    };

    const submitExchange = () => {
        setActivationError("");
        setCustomerError("");
        if (validateExchange()) setConfirmOpen(true);
    };

    /** 二次确认后提交 Vue 兑换请求，业务失败时保留当前弹窗。 */
    const confirmExchange = async () => {
        if (!customerId) return;
        setExchangeLoading(true);
        try {
            const result = await exchangeCode({ code: activationCode, customerId });
            if (result.errorCode !== ERROR_MESSAGE.REDEMPTION_SUCCESS.code) {
                setActivationError(ERROR_MESSAGE.getIntroByCode(result.errorCode) || "兑换失败");
                setConfirmOpen(false);
                return;
            }
            setConfirmOpen(false);
            setExchangeOpen(false);
            resetExchange();
            await load(query);
        } catch {
            setConfirmOpen(false);
            setActivationError("兑换失败");
        } finally {
            setExchangeLoading(false);
        }
    };

    /** 将 zhihao-ui-react 的排序方向转换为 Vue 充值分页接口参数。 */
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery: RechargeManageQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 充值记录列表的字段格式化逻辑。
    const columns: TableColumnProps<RechargeManageRow>[] = [
        { key: "id", label: "编号", width: 138, prop: "id", render: ({ row }) => row.id },
        {
            key: "customerInfo",
            label: "账号",
            minWidth: 228,
            prop: "customerInfo",
            render: ({ row }) => customerText(row.customerInfo)
        },
        {
            key: "memberLevel",
            label: "等级",
            width: 130,
            prop: "memberLevel",
            render: ({ row }) => MEMBER_LEVEL.getIntroByCode(row.memberLevel) || DEFAULT_VALUE
        },
        {
            key: "memberDay",
            label: "时长",
            width: 100,
            prop: "memberDay",
            render: ({ row }) => (row.memberDay == null ? DEFAULT_VALUE : `${row.memberDay}天`)
        },
        {
            key: "openMode",
            label: "支付/兑换方式",
            width: 150,
            prop: "openMode",
            render: ({ row }) => OPEN_MODE.getIntroByCode(row.openMode) || DEFAULT_VALUE
        },
        {
            key: "activationCode",
            label: "激活码",
            minWidth: 148,
            prop: "activationCode",
            render: ({ row }) => row.activationCode
        },
        {
            key: "amount",
            label: "实收金额（元）",
            minWidth: 130,
            prop: "amount",
            align: "right",
            headerAlign: "right",
            render: ({ row }) => (row.amount == null ? DEFAULT_VALUE : formatNumber(row.amount, 2))
        },
        {
            key: "activationDate",
            label: "激活日期",
            width: 110,
            prop: "activationDate",
            render: ({ row }) => (row.activationDate ? dateFormat(row.activationDate) : DEFAULT_VALUE)
        },
        {
            key: "expireDate",
            label: "到期日期",
            width: 110,
            prop: "expireDate",
            render: ({ row }) => (row.expireDate ? dateFormat(row.expireDate) : DEFAULT_VALUE)
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 150,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => (row.createdTime ? dateFormat(row.createdTime, DATETIME_PICKER_FORMAT) : DEFAULT_VALUE)
        }
    ];

    return (
        <section className="recharge-manage-page" aria-label="recharge-manage-page">
            <Card>
                <ZhTable<RechargeManageRow>
                    data={records}
                    columns={columns}
                    pagination={false}
                    rowKey="id"
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4}>会员充值记录</Typography.Title>

                            {error ? <Alert role="alert" title={error} type="error" /> : null}

                            <Space className="admin-table-filter-form mb-4">
                                <Permission anyOf={["ROLE_ORDER_W"]}>
                                    <Button
                                        aria-label="兑换"
                                        autoInsertSpace={false}
                                        type="primary"
                                        disabled={banButton}
                                        onClick={openExchange}
                                    >
                                        兑换
                                    </Button>
                                </Permission>
                                <Input
                                    aria-label="编号筛选"
                                    maxLength={50}
                                    placeholder="请输入编号搜索"
                                    value={idKeyword}
                                    onChange={event => setIdKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="账号信息"
                                    maxLength={50}
                                    placeholder="请输入账号信息搜索"
                                    value={keyword}
                                    onChange={event => setKeyword(event.target.value)}
                                />
                                <Select
                                    allowClear
                                    aria-label="会员等级筛选"
                                    options={MEMBER_LEVEL.options
                                        .filter(item => item.value !== MEMBER_LEVEL.PENDING.code)
                                        .map(option => ({
                                            label: option.label,
                                            value: String(option.value)
                                        }))}
                                    placeholder="请选择会员等级"
                                    value={memberLevel || undefined}
                                    onChange={value => setMemberLevel(value || "")}
                                />
                                <Select
                                    allowClear
                                    aria-label="支付方式筛选"
                                    options={OPEN_MODE.options.map(option => ({
                                        label: option.label,
                                        value: String(option.value)
                                    }))}
                                    placeholder="请选择支付方式"
                                    value={openMode || undefined}
                                    onChange={value => setOpenMode(value || "")}
                                />
                                <Input
                                    aria-label="激活码筛选"
                                    placeholder="请输入激活码搜索"
                                    value={activationCodeKeyword}
                                    onChange={event => setActivationCodeKeyword(event.target.value)}
                                />
                                <Button
                                    aria-label="查询"
                                    autoInsertSpace={false}
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={() =>
                                        setQuery({
                                            page: 1,
                                            size: query.size,
                                            ...(idKeyword.trim() ? { id: idKeyword.trim() } : {}),
                                            ...(keyword ? { customerKeyWord: keyword } : {}),
                                            ...(memberLevel ? { memberLevel: Number(memberLevel) } : {}),
                                            ...(openMode ? { openMode: Number(openMode) } : {}),
                                            ...(activationCodeKeyword ? { activationCode: activationCodeKeyword } : {})
                                        })
                                    }
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={() => {
                                        setIdKeyword("");
                                        setKeyword("");
                                        setMemberLevel("");
                                        setOpenMode("");
                                        setActivationCodeKeyword("");
                                        setQuery(current => ({ page: 1, size: current.size }));
                                    }}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                title="兑换"
                open={exchangeOpen}
                width={520}
                closeOnClickModal={false}
                closeOnPressEscape={false}
                onCancel={closeExchange}
                footer={
                    <Space>
                        <Button onClick={closeExchange}>取消</Button>
                        <Button loading={exchangeLoading} type="primary" onClick={submitExchange}>
                            提交
                        </Button>
                    </Space>
                }
            >
                <Typography.Paragraph>请输入 20 位激活码，查询后完成会员兑换。</Typography.Paragraph>
                <Form colon={false} layout="vertical">
                    <Form.Item
                        label="激活码"
                        required
                        validateStatus={activationError ? "error" : undefined}
                        help={activationError || undefined}
                    >
                        <Input
                            aria-label="激活码"
                            maxLength={20}
                            placeholder="请输入激活码"
                            value={activationCode}
                            onChange={event => void handleActivationCodeChange(event.target.value)}
                        />
                    </Form.Item>
                    {activationInfo ? (
                        <ZhGrid className="mt-4 rounded bg-blue-50 p-4" cols={3} gap={23}>
                            <ZhGridItem>
                                <ZhBaseItem
                                    label="等级"
                                    value={<>{MEMBER_LEVEL.getIntroByCode(activationInfo.level) || DEFAULT_VALUE}</>}
                                />
                            </ZhGridItem>
                            <ZhGridItem>
                                <ZhBaseItem
                                    label="时长"
                                    value={
                                        <>
                                            {activationInfo.duration == null
                                                ? DEFAULT_VALUE
                                                : `${activationInfo.duration}天`}
                                        </>
                                    }
                                />
                            </ZhGridItem>
                            {activationInfo.state === ACTIVATION_CODE_STATE.UNUSED.code && activationInfo.hasBind ? (
                                <>
                                    <ZhGridItem>
                                        <ZhBaseItem
                                            label="用户ID"
                                            value={<>{activationInfo.customerInfo?.code || DEFAULT_VALUE}</>}
                                        />
                                    </ZhGridItem>
                                    <ZhGridItem>
                                        <ZhBaseItem
                                            label="个人实名/机构名称"
                                            value={
                                                <>
                                                    {activationInfo.customerInfo?.institutionName ||
                                                        activationInfo.customerInfo?.realName ||
                                                        DEFAULT_VALUE}
                                                </>
                                            }
                                        />
                                    </ZhGridItem>
                                    <ZhGridItem>
                                        <ZhBaseItem
                                            label="手机号"
                                            value={<>{activationInfo.customerInfo?.mobile || DEFAULT_VALUE}</>}
                                        />
                                    </ZhGridItem>
                                </>
                            ) : null}
                        </ZhGrid>
                    ) : null}
                    {activationInfo?.state === ACTIVATION_CODE_STATE.UNUSED.code && !activationInfo.hasBind ? (
                        <div className="mt-4">
                            <Form.Item
                                label="关联账号"
                                required
                                validateStatus={customerError || customerSearchError ? "error" : undefined}
                                help={customerError || customerSearchError || undefined}
                            >
                                <Input
                                    aria-label="关联账号"
                                    placeholder="请输入账号信息搜索"
                                    onFocus={event => {
                                        if (!event.currentTarget.value.trim() && !customerOptions.length) {
                                            void handleCustomerSearch("");
                                        }
                                    }}
                                    onChange={event => void handleCustomerSearch(event.target.value)}
                                />
                            </Form.Item>
                            {customerOptions.map(option => (
                                <div
                                    key={option.id}
                                    role="option"
                                    aria-selected={customerId === option.id}
                                    className="cursor-pointer p-2"
                                    onClick={() => {
                                        setCustomerId(option.id);
                                        setCustomerError("");
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </Form>
            </ZhDialog>
            <ZhDialog
                title="是否确认兑换"
                open={confirmOpen}
                width={520}
                closeOnClickModal={false}
                closeOnPressEscape={false}
                onCancel={() => setConfirmOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmOpen(false)}>取消</Button>
                        <Button loading={exchangeLoading} type="primary" onClick={() => void confirmExchange()}>
                            确认兑换
                        </Button>
                    </Space>
                }
            >
                账号兑换后将获得 {activationInfo?.duration} 天{MEMBER_LEVEL.getIntroByCode(activationInfo?.level)}
                ，是否确认兑换？
            </ZhDialog>
        </section>
    );
}

export default RechargeManagePage;
